// src/components/ChatView.js
// 1:1 conversation with a character bot. Shows the character's personality card
// at the top (like the mobile app), sends a locally-matched memory_hint with
// each message, supports regenerate / delete / clear, and opens the Memory Brain.

import { useEffect, useRef, useState } from "react";
import Avatar from "./Avatar";
import MemoryGraph from "./MemoryGraph";
import { bots as botsApi, chat as chatApi, memory as memoryApi } from "../api";
import { findRelevantMemory } from "../lib/memoryHint";

export default function ChatView({ bot, onBack, onQuota }) {
  const [messages, setMessages] = useState([]);
  const [detail, setDetail] = useState(bot); // full bot (personality_prompt, first_message)
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [toast, setToast] = useState("");
  const [showGraph, setShowGraph] = useState(false);
  const [showPersona, setShowPersona] = useState(false);
  const scroller = useRef(null);
  const lastSentRef = useRef(0);
  const structuredRef = useRef(null); // cached structured memory for memory_hint

  const flash = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(""), 2500);
  };

  const scrollDown = () =>
    requestAnimationFrame(() => {
      if (scroller.current) scroller.current.scrollTop = scroller.current.scrollHeight;
    });

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const [d, hist] = await Promise.all([
          botsApi.detail(bot.id).catch(() => bot),
          chatApi.history(bot.id).catch(() => ({ messages: [] })),
        ]);
        if (!alive) return;
        setDetail(d);
        let msgs = hist.messages || [];
        if (msgs.length === 0 && d.first_message) msgs = [{ role: "assistant", content: d.first_message }];
        setMessages(msgs);
        scrollDown();
      } finally {
        if (alive) setLoading(false);
      }
      // Cache structured memory for local memory_hint matching (mobile parity).
      memoryApi.get(bot.id).then((m) => alive && (structuredRef.current = m.structured || {})).catch(() => {});
    })();
    return () => {
      alive = false;
    };
  }, [bot.id]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setErr("");
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }]);
    setSending(true);
    scrollDown();
    try {
      const wait = 1600 - (Date.now() - lastSentRef.current); // avoid backend 429
      if (wait > 0) await new Promise((r) => setTimeout(r, wait));
      lastSentRef.current = Date.now();

      const memory_hint = findRelevantMemory(text, structuredRef.current, messages);
      const res = await chatApi.send(bot.id, text, { memory_hint });
      if (Array.isArray(res.messages) && res.messages.length) setMessages(res.messages);
      else if (res.response) setMessages((m) => [...m, { role: "assistant", content: res.response }]);
      onQuota && onQuota({ messages_remaining: res.messages_remaining });
    } catch (e) {
      const d = e.detail;
      setErr(typeof d === "string" ? d : d?.message || e.message);
      setMessages((m) => m.slice(0, -1));
      setInput(text);
    } finally {
      setSending(false);
      scrollDown();
    }
  };

  const regenerate = async () => {
    if (sending) return;
    const lastUser = [...messages].reverse().find((m) => m.role === "user");
    if (!lastUser) return;
    setErr("");
    setSending(true);
    scrollDown();
    try {
      const res = await chatApi.regenerate(bot.id, lastUser.content);
      if (Array.isArray(res.messages) && res.messages.length) setMessages(res.messages);
      else if (res.response) {
        setMessages((m) => {
          const c = [...m];
          if (c[c.length - 1]?.role === "assistant") c[c.length - 1] = { role: "assistant", content: res.response };
          else c.push({ role: "assistant", content: res.response });
          return c;
        });
      }
    } catch (e) {
      const d = e.detail;
      setErr(typeof d === "string" ? d : d?.message || e.message);
    } finally {
      setSending(false);
      scrollDown();
    }
  };

  const clearChat = async () => {
    if (!window.confirm(`Clear your whole conversation with ${bot.name}?`)) return;
    try {
      await chatApi.clear(bot.id);
      setMessages(detail.first_message ? [{ role: "assistant", content: detail.first_message }] : []);
      flash("Chat cleared");
    } catch (e) {
      setErr(e.message);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <div className="chat">
      <header className="chat__head">
        <button className="iconbtn" onClick={onBack} aria-label="Back">←</button>
        <button className="chat__whobtn" onClick={() => setShowPersona(true)} title="About this character">
          <Avatar name={bot.name} url={bot.avatar_url} size={40} />
          <div className="chat__who">
            <div className="chat__name">{bot.name}</div>
            <div className="chat__sub">{bot.category || "companion"}</div>
          </div>
        </button>
        <button className="iconbtn" style={{ marginLeft: "auto" }} title="Memory brain" onClick={() => setShowGraph(true)}>🧠</button>
        <button className="btn btn--ghost chat__delete" title="Delete chat" onClick={clearChat}>🗑 Delete chat</button>
      </header>

      <div className="chat__scroll" ref={scroller}>
        {loading ? (
          <div className="chat__loading">Loading conversation…</div>
        ) : (
          <>
            {detail.personality_prompt && (
              <div className="chat__persona">
                <div className="chat__persona-head">✨ Character &amp; Personality</div>
                <div className="chat__persona-body">{detail.personality_prompt}</div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`bubble bubble--${m.role}`}>
                {m.role === "assistant" && <Avatar name={bot.name} url={bot.avatar_url} size={28} />}
                <div className="bubble__text">{m.content}</div>
              </div>
            ))}
          </>
        )}
        {sending && (
          <div className="bubble bubble--assistant">
            <Avatar name={bot.name} url={bot.avatar_url} size={28} />
            <div className="bubble__text typing"><span></span><span></span><span></span></div>
          </div>
        )}
      </div>

      {toast && <div className="chat__toast">{toast}</div>}
      {err && <div className="form-error center">{err}</div>}

      <div className="composer">
        <button
          className="iconbtn composer__regen"
          title="Regenerate last reply"
          onClick={regenerate}
          disabled={sending || !messages.some((m) => m.role === "assistant")}
        >↻</button>
        <textarea rows={1} placeholder={`Message ${bot.name}…`} value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={onKey} />
        <button className="btn btn--primary" onClick={send} disabled={sending || !input.trim()}>Send</button>
      </div>

      {showGraph && <MemoryGraph botId={bot.id} botName={bot.name} onClose={() => setShowGraph(false)} />}

      {showPersona && (
        <div className="modal-backdrop" onClick={() => setShowPersona(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal__tabs"><button className="active">About {bot.name}</button></div>
            <div className="modal__body">
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <Avatar name={bot.name} url={bot.avatar_url} size={56} />
                <div>
                  <div style={{ fontWeight: 700 }}>{bot.name}</div>
                  <div style={{ fontSize: 13, color: "var(--text-dim)" }}>{detail.description}</div>
                </div>
              </div>
              {detail.personality_prompt && (
                <>
                  <div className="chat__persona-head">✨ Character &amp; Personality</div>
                  <div className="chat__persona-body">{detail.personality_prompt}</div>
                </>
              )}
              {detail.scenario && (
                <>
                  <div className="chat__persona-head">🎬 Scenario</div>
                  <div className="chat__persona-body">{detail.scenario}</div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
